'use client';

import { useEffect, useState } from 'react';
import {
  ArrowDown01Icon,
  ArrowRight01Icon,
  Globe02Icon,
} from '@terra/ui-icons';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import { useTranslations } from 'next-intl';
import { Button } from '../../../components/button';
import {
  BodyDefault,
  MetaDefault,
  MonoSmall,
} from '../../../components/typography';
import { Spinner } from '../../../components/spinner';
import type { ToolExecutionHar } from '@terra/shared-contracts-tracker';
import { ToolHarCredentialBadge } from '../../chat/components/ToolHarCredentialBadge';
import { useChatHar } from '../../chat/har/ChatHarProvider';
import { formatBytes } from '../../../utils/formatBytes';
import { renderHttpMethodText } from '../../../utils/http-badge.utils';
import { getUrlPath } from '../../../utils/url-path.utils';
import { ToolHarRequestResponse } from '../../chat/components/ToolHarRequestResponse';

export type HttpArtifactProps = {
  toolCallId: string;
  toolType: 'HTTP' | 'PYTHON';
  showSummaryHeader?: boolean;
  embedded?: boolean;
};
type HttpArtifactHarEntrySummaryProps = {
  har: ToolExecutionHar;
  isExpanded: boolean;
  onActivate: () => void;
};

function HttpArtifactHarEntrySummary({
  har,
  isExpanded,
  onActivate,
}: HttpArtifactHarEntrySummaryProps) {
  const t = useTranslations('designSystem.chat');
  const httpRequest = har.harEntry.request;
  const httpResponse = har.harEntry.response;

  return (
    <Button
      variant="ghost"
      size="sm"
      width="full"
      h="28px"
      px={2}
      justifyContent="space-between"
      borderRadius="0"
      aria-expanded={isExpanded}
      onClick={(event) => {
        event.stopPropagation();
        onActivate();
      }}
    >
      <HStack gap={1.5} flex="1" minW={0}>
        <Box
          display="flex"
          alignItems="center"
          color="fg.muted"
          flexShrink={0}
          aria-hidden
        >
          {isExpanded ? (
            <ArrowDown01Icon size={10} />
          ) : (
            <ArrowRight01Icon size={10} />
          )}
        </Box>
        {renderHttpMethodText(httpRequest.method)}
        <MonoSmall truncate maxW="300px" title={httpRequest.url}>
          {getUrlPath(httpRequest.url)}
        </MonoSmall>
        {renderHttpMethodText(httpResponse.status)}
      </HStack>

      <HStack gap={1.5} flexShrink={0}>
        <ToolHarCredentialBadge har={har} showSeparator />
        <MetaDefault color="fg.muted">
          {formatBytes(getResponseSize(har))}
        </MetaDefault>
        <MetaDefault color="fg.muted">•</MetaDefault>
        <MetaDefault color="fg.muted">
          {t('runtimeMs', { ms: Math.round(har.harEntry.time) })}
        </MetaDefault>
      </HStack>
    </Button>
  );
}

type HttpArtifactRequestResponseProps = {
  har: ToolExecutionHar;
  hideSummaryRow?: boolean;
};

function HttpArtifactRequestResponse({
  har,
  hideSummaryRow,
}: HttpArtifactRequestResponseProps) {
  return (
    <ToolHarRequestResponse har={har} hideHarSummaryRow={hideSummaryRow} />
  );
}

type HttpArtifactExpandedViewProps = {
  har: ToolExecutionHar;
  hideSummaryRow?: boolean;
};

function HttpArtifactExpandedView({
  har,
  hideSummaryRow,
}: HttpArtifactExpandedViewProps) {
  return (
    <HttpArtifactRequestResponse har={har} hideSummaryRow={hideSummaryRow} />
  );
}

type HttpArtifactTimelineProps = {
  hars: ToolExecutionHar[];
  showSummaryHeader: boolean;
  embedded?: boolean;
};

function HttpArtifactTimeline({
  hars,
  showSummaryHeader,
  embedded,
}: HttpArtifactTimelineProps) {
  const t = useTranslations('designSystem.chat');
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  if (hars.length === 1) {
    return (
      <Box
        borderWidth={embedded ? '0' : '1px'}
        borderColor="border.neutral.muted"
        borderRadius={embedded ? '0' : 'md'}
        bg="transparent"
        overflow="hidden"
        width="100%"
      >
        {showSummaryHeader ? (
          <HStack
            justify="space-between"
            alignItems="center"
            px={2}
            h="28px"
            bg="transparent"
            borderBottomWidth="1px"
            borderBottomColor="border.neutral.muted"
          >
            <HStack gap={1.5} flex="1" minW={0}>
              {renderHttpMethodText(hars[0].harEntry.request.method)}
              <MonoSmall
                truncate
                maxW="300px"
                title={hars[0].harEntry.request.url}
              >
                {getUrlPath(hars[0].harEntry.request.url)}
              </MonoSmall>
              {renderHttpMethodText(hars[0].harEntry.response.status)}
            </HStack>
            <HStack gap={1.5} flexShrink={0}>
              <ToolHarCredentialBadge har={hars[0]} showSeparator />
              <MetaDefault color="fg.muted">
                {formatBytes(getResponseSize(hars[0]))}
              </MetaDefault>
              <MetaDefault color="fg.muted">•</MetaDefault>
              <MetaDefault color="fg.muted">
                {t('runtimeMs', { ms: Math.round(hars[0].harEntry.time) })}
              </MetaDefault>
            </HStack>
          </HStack>
        ) : null}
        <HttpArtifactExpandedView har={hars[0]} hideSummaryRow />
      </Box>
    );
  }

  return (
    <Box
      borderWidth={embedded ? '0' : '1px'}
      borderColor="border.neutral.muted"
      borderRadius={embedded ? '0' : 'md'}
      bg="transparent"
      overflow="hidden"
      width="100%"
    >
      {showSummaryHeader ? (
        <HStack
          justify="space-between"
          alignItems="center"
          px={2}
          h="28px"
          bg="transparent"
          borderBottomWidth="1px"
          borderBottomColor="border.neutral.muted"
        >
          <MetaDefault>
            {t('httpRequestsCount', { count: hars.length })}
          </MetaDefault>
          <MetaDefault color="fg.muted">
            {t('totalDuration', {
              duration: t('runtimeMs', {
                ms: Math.round(calculateTotalDuration(hars)),
              }),
            })}
          </MetaDefault>
        </HStack>
      ) : null}

      <VStack gap={0} alignItems="stretch" width="100%">
        {hars.map((har, index) => {
          const isExpanded = expandedIndex === index;

          return (
            <Box
              key={`${har.toolCallId}-${index}`}
              bg="transparent"
              borderTopWidth={index > 0 ? '1px' : '0'}
              borderTopColor="border.neutral.muted"
              width="100%"
            >
              <HttpArtifactHarEntrySummary
                har={har}
                isExpanded={isExpanded}
                onActivate={() => setExpandedIndex(isExpanded ? null : index)}
              />

              {isExpanded ? (
                <Box
                  borderTopWidth="1px"
                  borderColor="border.neutral.muted"
                  bg="transparent"
                >
                  <HttpArtifactExpandedView har={har} hideSummaryRow />
                </Box>
              ) : null}
            </Box>
          );
        })}
      </VStack>
    </Box>
  );
}

function calculateTotalDuration(hars: ToolExecutionHar[]): number {
  if (hars.length === 0) return 0;
  if (hars.length === 1) return hars[0].harEntry.time;
  const startTime = new Date(hars[0].timestamp).getTime();

  return Math.max(
    ...hars.map(
      (har) =>
        new Date(har.timestamp).getTime() - startTime + har.harEntry.time,
    ),
  );
}

function getResponseSize(har: ToolExecutionHar): number {
  const httpResponse = har.harEntry.response;

  if (httpResponse.bodySize !== undefined && httpResponse.bodySize >= 0) {
    return httpResponse.bodySize;
  }

  if (
    httpResponse.content?.size !== undefined &&
    httpResponse.content.size >= 0
  ) {
    return httpResponse.content.size;
  }

  return 0;
}

export function HttpArtifact({
  toolCallId,
  showSummaryHeader = true,
  embedded,
}: HttpArtifactProps) {
  const t = useTranslations('designSystem.chat');
  const { fetchHarData: onFetchHarData } = useChatHar();
  const [hars, setHars] = useState<ToolExecutionHar[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;
    let pollInterval: NodeJS.Timeout | null = null;
    let attemptCount = 0;
    const maxAttempts = 3;
    const fetchHarData = async () => {
      attemptCount++;

      try {
        const data = await onFetchHarData(toolCallId);

        if (!isMounted) return;
        setHars(data);
        if (data.length > 0) {
          setIsLoading(false);
          if (pollInterval) {
            clearInterval(pollInterval);
            pollInterval = null;
          }

          return;
        }

        if (attemptCount >= maxAttempts) {
          setIsLoading(false);
          if (pollInterval) {
            clearInterval(pollInterval);
            pollInterval = null;
          }
        }
      } catch (err) {
        if (!isMounted) return;
        console.error('[HttpArtifact] Error fetching HAR data:', err);
        setError('Failed to load HTTP request data');
        setIsLoading(false);
        if (pollInterval) {
          clearInterval(pollInterval);
          pollInterval = null;
        }
      }
    };

    fetchHarData();
    pollInterval = setInterval(fetchHarData, 5000);

    return () => {
      isMounted = false;
      if (pollInterval) {
        clearInterval(pollInterval);
      }
    };
  }, [toolCallId, onFetchHarData]);
  if (isLoading) {
    return (
      <HStack justify="center" p={4} gap={2}>
        <Spinner size="sm" />
        <BodyDefault color="fg.muted">
          {t('waitingForHttpRequests')}
        </BodyDefault>
      </HStack>
    );
  }

  if (error) {
    return (
      <HStack justify="center" p={4} gap={2}>
        <BodyDefault color="fg.error">{error}</BodyDefault>
      </HStack>
    );
  }

  if (hars.length === 0) {
    return (
      <HStack justify="center" p={4} gap={2}>
        <Globe02Icon size={16} />
        <BodyDefault color="fg.muted">
          {t('noHttpRequestsCaptured')}
        </BodyDefault>
      </HStack>
    );
  }

  return (
    <HttpArtifactTimeline
      hars={hars}
      showSummaryHeader={showSummaryHeader}
      embedded={embedded}
    />
  );
}
